import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FormUtilsService } from '@app/@core';
import { ConsumePasswordResetTokenGQL } from '@app/@graphql';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { Observable, BehaviorSubject, forkJoin, throwError } from 'rxjs';
import { catchError, delay, switchMap, tap } from 'rxjs/operators';
import { ChangePassword, ChangePasswordValidationMessages } from '../models';
import { TranslateService } from '@ngx-translate/core';
import { NbToastrService } from '@nebular/theme';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Router } from '@angular/router';

@Injectable()
export class ChangePasswordPageService extends OnDestroyMixin {
  private _loading$ = new BehaviorSubject<boolean>(false);
  readonly loading$: Observable<boolean>;
  readonly form: UntypedFormGroup;
  readonly validationMessages: ChangePasswordValidationMessages;

  constructor(
    private formUtils: FormUtilsService,
    private router: Router,
    private translate: TranslateService,
    private toastr: NbToastrService,
    private consumeResetTokenGQL: ConsumePasswordResetTokenGQL
  ) {
    super();
    this.form = this.buildForm();
    this.validationMessages = this.buildValidationMessages();
  }

  save(email: string, token: string, password: string) {
    this._loading$.next(true);

    this.consumeResetTokenGQL.mutate({
      email: email,
      token: token,
      newPassword: password
    }).pipe(
      switchMap(() =>
        forkJoin([
          this.translate.get(marker('change_password_success_message')),
          this.translate.get(marker('change_password_success_title'))
        ]).pipe(
          tap(([message, title]) => this.toastr.success(message, title)),
          tap(() => {
            this._loading$.next(false);
          }),
          delay(5000),
          tap(() => {
            this.router.navigate(['auth/login']);
          })
        )
      ),
      catchError(err => {
        this._loading$.next(false);
        return throwError(err);
      }),
      untilComponentDestroyed(this)
    ).subscribe();
  }

  private buildForm() {
    const password = new UntypedFormControl(null, Validators.compose([Validators.required, Validators.minLength(5)]));
    const confirmPassword = new UntypedFormControl(null, this.equalTo(password));

    return new UntypedFormGroup(
      {
        password,
        confirmPassword
      },
      {
        updateOn: 'change'
      }
    );
  }

  private buildValidationMessages(): ChangePasswordValidationMessages {
    return {
      password: [
        { type: 'required', message: marker('err_field_required') },
        { type: 'minlength', message: marker('err_password_min_length') }
      ],
      confirmPassword: [{ type: 'equalTo', message: marker('err_password_confirm') }]
    };
  }


  private equalTo(otherControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !otherControl.value) return null;
      return control.value === otherControl.value ? null : { equalTo: true };
    };
  }
}
